import React, { useEffect, useContext } from "react";
import {
  AreaChart,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  Legend,
  Area,
  CartesianGrid,
  YAxis
} from "recharts";

import { useBlynkApi } from "../hooks/useBlynkApi";
import "./Graph.css";
import TokensContext from '../TokensContext';


/**
 * Converts a timestamp to a string in the format "HH:MM"
 * @param {int} timestamp the timestamp to format
 */
const formatTimeStamp = (timestamp) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

const formatTemperature = (temp) => {
  return `${temp.toFixed(1)}°C`;
};

/**
 * go over the filtered data and fill in any missing data points with a temperature of 0
 */
const fillMissingDataPoints = (data, expectedDataPointInterval) => {
  if (data.length <= 1)
    return [];

  const filledData = [];
    
  //to the first one
  let nextExpectedTimestamp = data[0].time;
  
  for (let i = 0; i < data.length; i++) {
    const currentDataPoint = data[i];

    //if current data point is not the expected timestamp, add a data point with a temperature of 0
    while (currentDataPoint.time !== nextExpectedTimestamp) {
      filledData.push({
        time: nextExpectedTimestamp,
        temperature: null,
      });

      //increment the next expected timestamp by the expected interval
      nextExpectedTimestamp += expectedDataPointInterval;
    }

    //add the current data point to the filled data
    filledData.push(currentDataPoint);

    //increment the next expected timestamp by the expected interval
    nextExpectedTimestamp += expectedDataPointInterval;
  }

  //fill the remainder of filledData till the current time stamp also with null
  const currentTimeStamp = Date.now();
  while (nextExpectedTimestamp <= currentTimeStamp) {
    filledData.push({
      time: nextExpectedTimestamp,
      temperature: null,
    });

    nextExpectedTimestamp += expectedDataPointInterval;
  }

  return filledData;
}

const filterData = (data, dataTimeBreak) => {
  return data.filter(
    (item) => item.time >= dataTimeBreak
  );
}
const fillAndFilterData = (data, dataTimeBreak, expectedDataPointInterval) => {
  data = filterData(data, dataTimeBreak)
  return fillMissingDataPoints(data, expectedDataPointInterval);
}

const mergeData = (mainData, positionData) => {

  if (mainData[0].time !== positionData[0].time) { //TODO: if there's more data in positions than in temp, an exception is thrown here as time is null
    console.warn("times of mainData and positionData don't match on first item");
  }
  //same check at the end of the arrays
  if (mainData[mainData.length - 1].time !== positionData[positionData.length - 1].time) {
    console.warn("times of mainData and positionData don't match on last item");
  }

  //TODO: this is using the index to place the item, but if there's more data at the start for one of the two, the data will be out of sync.
  return mainData.map((item, index) => ({
    ...item,
    position: positionData[index]?.position || null,
  }));
}

export default function Graph() {
  const { heaters } = useContext(TokensContext);
  const { getTemperatureHistory, getPositionHistory } = useBlynkApi(heaters[0].token); //TODO: hardcoded to use position of first heater.
  const [graphData, setGraphData] = React.useState([]);

  //expectedDataPointInterval is the expected time interval between each data point
  const expectedDataPointInterval = 60 * 1000;

  useEffect(()=> {
    const fetchData = async () => {
      const temperatureData = await getTemperatureHistory();
      const positionData = await getPositionHistory();

      const lastEntryTime = Math.max(temperatureData[temperatureData.length - 1]?.time, positionData[positionData.length - 1]?.time);
      //get the timestamp of 6 hours ago
      const sixHoursAgo = lastEntryTime - 6 * 60 * 60 * 1000;

      const tempFilledData = fillAndFilterData(temperatureData, sixHoursAgo, expectedDataPointInterval);
      //we could avoid the second fill here because we're going to merge the data anyway and discard the timestamp. but that makes the merge code very complicated, so we're taking a more brute force approach
      const posFilledData = fillAndFilterData(positionData, sixHoursAgo, expectedDataPointInterval);
      var graphData;
      
      if (posFilledData.length > 1)
        graphData = mergeData(tempFilledData, posFilledData);
      else
        graphData = tempFilledData;
      setGraphData(graphData);

    };

    fetchData();
  }, [getTemperatureHistory, getPositionHistory, expectedDataPointInterval]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="recharts-default-tooltip custom-tooltip" >
          <p className="label">{`Hora: ${formatTimeStamp(label)}`}</p>
          {payload[0] && <p className="label">{`Temperatura: ${formatTemperature(payload[0].value)}`}</p>}
          {payload[1] && <p className="label">{`Posición: ${Math.round(payload[1].value)}`}</p>}
        </div>
      );
  }
  return null;
};

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={graphData}
      margin={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}>
        <defs>
          <linearGradient id="tempGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="orange"/>
            <stop offset="50%" stopColor="yellow"/>
            <stop offset="100%" stopColor="green"/>
          </linearGradient>
          <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="orange" stopOpacity={0.2} />
            <stop offset="40%" stopColor="white" stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id="blueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0045EA" stopOpacity={0.2} />
            <stop offset="40%" stopColor="white" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" type="number" scale="time" tickFormatter={formatTimeStamp} domain={['auto', 'auto']} axisLine={false}/>
        <Tooltip
          content={<CustomTooltip />}
          labelFormatter={(time) => 'Hora: ' + formatTimeStamp(time)}
          formatter={(value) => formatTemperature(Number(value))}
        />
        <Legend 
          formatter={(value) => <span className="legend-text">{value}</span>}
          />

        <YAxis domain={[10,30]} axisLine={false} tick={false} hide={true} />
        <Area type="monotone" dataKey="temperature" stroke="url(#tempGradient)" fill="url(#orangeGradient)" name="Temperatura" />

        <YAxis yAxisId="right" orientation="right" domain={[1, 3]} hide={true} />
        <Area type="monotone" dataKey="position" stroke="#0045EA" tooltipType="" fill="url(#blueGradient)" name="Posición" yAxisId="right" />
      </AreaChart>
    </ResponsiveContainer>
  );
}
